import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";

// "How it fits together" (docs/tools-catalog-design.md): the three things a map suggests and
// where each comes from — the full version on Home, a one-line reminder on Standards and Tools,
// and the Registry's own one-line intro.

/** The one-line reminder, for the top of the Standards and Tools pages. */
export function FitsReminder() {
  return (
    <p className="mb-4 max-w-4xl text-sm text-text/60">
      <Link to="/standards" className="font-medium text-text/80 hover:underline">
        Standards
      </Link>{" "}
      say what's required. <span className="font-medium text-text/80">Practices</span> (from the Tech Radar) are
      recognised ways to meet them.{" "}
      <Link to="/tools" className="font-medium text-text/80 hover:underline">
        Tools
      </Link>
      , platforms, libraries and languages are the things you use, each rated by the Tech Radar (Adopt, Trial, Assess,
      Hold).
    </p>
  );
}

/** The one-line intro for the top of the Registry page, in the same voice as `FitsReminder`. */
export function RegistryIntro() {
  return (
    <p className="mb-4 max-w-4xl text-sm text-text/60">
      <span className="font-medium text-text/80">Capabilities</span> are what Guild's platform does: each one's scope,
      inputs and outputs, and where its boundaries lie. A capability map matches a requirement to them, then suggests
      the{" "}
      <Link to="/standards" className="font-medium text-text/80 hover:underline">
        standards
      </Link>{" "}
      and{" "}
      <Link to="/tools" className="font-medium text-text/80 hover:underline">
        tools
      </Link>{" "}
      that apply.
    </p>
  );
}

const SOURCES: [string, string, string][] = [
  ["Capabilities", "this registry", "everything"],
  ["Standards", "Confluence (Minimum Technology Standards)", "a transcription"],
  ["Radar rings, descriptions, write-ups", "Jira RADAR project and Confluence", "a dated copy, re-synced"],
  ["Tool inventory, licensing, department", "Trelica", "the link and logo; the rest read live"],
  [
    "Which tool meets which standard or supports which capability",
    "this registry",
    "links the model drafts and a person confirms",
  ],
];

/** The full explanation, for Home: what each kind of entry is for, how a map uses them, and
 * which system owns what the registry shows. */
export function HowItFitsTogether() {
  return (
    <Card className="flex flex-col gap-3">
      <h3 className="text-xs font-semibold tracking-wide text-text/60 uppercase">How it fits together</h3>
      <p className="leading-relaxed">
        <strong>Standards</strong> say what's required. <strong>Practices</strong> (from the Tech Radar) are recognised
        ways to meet them. <strong>Tools, platforms, libraries and languages</strong> are the things you use, each rated
        by the Tech Radar (Adopt, Trial, Assess, Hold).
      </p>
      <p className="text-sm text-text/70">
        A capability map suggests from all three: tools a standard on the map requires, tools used to deliver a
        capability it touches, and others that may help (ranked by ring, never Hold or Deprecated), plus a note of
        anything the catalog doesn't cover.
      </p>
      <table className="w-full border-collapse text-left text-[13px]">
        <thead>
          <tr className="bg-sunken text-xs tracking-wide text-text/60 uppercase">
            <th className="px-2.5 py-1.5 font-semibold">Thing</th>
            <th className="px-2.5 py-1.5 font-semibold">Source of truth</th>
            <th className="px-2.5 py-1.5 font-semibold">The registry keeps</th>
          </tr>
        </thead>
        <tbody>
          {SOURCES.map(([thing, source, keeps]) => (
            <tr key={thing} className="border-b border-line">
              <td className="px-2.5 py-1.5">{thing}</td>
              <td className="px-2.5 py-1.5 text-text/70">{source}</td>
              <td className="px-2.5 py-1.5 text-text/70">{keeps}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
